import React, { useState, useEffect } from 'react';
import { fetchAllRegularUsers, processDeposit, getDepositHistory, verifyAdmin } from '../../services/adminService';
import { useAdminAuth } from '../../hooks/useAdminAuth';

function DepositManagement() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [depositHistory, setDepositHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { isAdmin } = useAdminAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedUser && showHistory) {
      loadDepositHistory(selectedUser.id);
    }
  }, [selectedUser, showHistory]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const adminStatus = await verifyAdmin();
      if (!adminStatus.isAdmin) {
        throw new Error('Unauthorized: Admin access required');
      }
      const usersList = await fetchAllRegularUsers();
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      setError(error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadDepositHistory = async (userId) => {
    try {
      const history = await getDepositHistory(userId);
      setDepositHistory(history);
    } catch (error) {
      console.error('Error loading deposit history:', error);
      setError('Failed to load deposit history');
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setDepositAmount('');
    setReason('');
    setSuccess('');
    setError('');
    setShowHistory(false);
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!selectedUser) {
      setError('Please select a user first');
      return;
    }

    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      setError('Please enter a valid deposit amount');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for this deposit');
      return;
    }

    setProcessing(true);

    try {
      const result = await processDeposit(selectedUser.id, amount, reason);
      setSuccess(result.message);
      setDepositAmount('');
      setReason('');
      
      // Reload users to get updated balances
      await loadUsers();
      
      // Update selected user balance
      // Calculate new total: Total = Wallet + Commission (excluding frozen)
      const newTotal = result.newBalance + (selectedUser.balances?.commission || 0);
      setSelectedUser({
        ...selectedUser,
        balances: {
          ...selectedUser.balances,
          wallet: result.newBalance,
          total: newTotal
        }
      });

      // Reload deposit history
      if (showHistory) {
        await loadDepositHistory(selectedUser.id);
      }
    } catch (error) {
      console.error('Error processing deposit:', error);
      setError(error.message || 'Failed to process deposit');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('en-US');
    }
    return new Date(timestamp).toLocaleString('en-US');
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(search) ||
      user.username?.toLowerCase().includes(search) ||
      user.id?.toLowerCase().includes(search)
    );
  });

  if (!isAdmin) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Unauthorized: Admin access required
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="admin-deposit-management">
      <div className="admin-deposit-header">
        <div>
          <h2 className="admin-deposit-title">
            <i className="bi bi-wallet2 me-2"></i>
            Deposit Management
          </h2>
          <p className="admin-deposit-subtitle">Process manual deposits for user accounts</p>
        </div>
        <button className="admin-deposit-refresh-btn" onClick={loadUsers} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      {error && (
        <div className="admin-deposit-message admin-deposit-error">
          <i className="bi bi-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="admin-deposit-message admin-deposit-success">
          <i className="bi bi-check-circle me-2"></i>
          {success}
        </div>
      )}

      <div className="admin-deposit-layout">
        {/* User Selection Panel */}
        <div className="admin-deposit-users-panel">
          <div className="admin-deposit-panel-header">
            <h3>
              <i className="bi bi-people me-2"></i>
              Select User
            </h3>
            <div className="admin-deposit-search">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search by email, username, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-deposit-search-input"
              />
            </div>
          </div>
          <div className="admin-deposit-users-list">
            {filteredUsers.length === 0 ? (
              <div className="admin-deposit-empty">
                <i className="bi bi-inbox"></i>
                <p>No users found</p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`admin-deposit-user-card ${selectedUser?.id === user.id ? 'active' : ''}`}
                  onClick={() => handleUserSelect(user)}
                >
                  <div className="admin-deposit-user-info">
                    <div className="admin-deposit-user-email">{user.email || 'N/A'}</div>
                    <div className="admin-deposit-user-details">
                      <span className="admin-deposit-user-name">{user.username || 'No username'}</span>
                      <span className="admin-deposit-user-vip">{user.vipLevel || 'VIP1'}</span>
                    </div>
                  </div>
                  <div className="admin-deposit-user-balance">
                    <div className="admin-deposit-balance-label">Balance</div>
                    <div className="admin-deposit-balance-value">
                      {formatCurrency(user.balances?.wallet || 0)} USDT
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Deposit Form Panel */}
        <div className="admin-deposit-form-panel">
          {selectedUser ? (
            <>
              <div className="admin-deposit-panel-header">
                <h3>
                  <i className="bi bi-wallet me-2"></i>
                  Process Deposit
                </h3>
                <button
                  className="admin-deposit-history-toggle"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <i className={`bi bi-${showHistory ? 'x' : 'clock-history'} me-2`}></i>
                  {showHistory ? 'Hide' : 'Show'} History
                </button>
              </div>

              {showHistory ? (
                <div className="admin-deposit-history-panel">
                  <h4>Deposit History for {selectedUser.email}</h4>
                  {depositHistory.length === 0 ? (
                    <div className="admin-deposit-empty">
                      <i className="bi bi-clock-history"></i>
                      <p>No deposit history</p>
                    </div>
                  ) : (
                    <div className="admin-deposit-history-list">
                      {depositHistory.map((deposit) => (
                        <div key={deposit.id} className="admin-deposit-history-item">
                          <div className="admin-deposit-history-amount">
                            <i className="bi bi-plus-circle text-success me-2"></i>
                            +{formatCurrency(deposit.amount)} USDT
                          </div>
                          <div className="admin-deposit-history-details">
                            <div className="admin-deposit-history-reason">{deposit.reason}</div>
                            <div className="admin-deposit-history-meta">
                              <span>By: {deposit.processedBy}</span>
                              <span>{formatDate(deposit.timestamp)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleDeposit} className="admin-deposit-form">
                  <div className="admin-deposit-user-summary">
                    <div className="admin-deposit-summary-item">
                      <label>User Email</label>
                      <div className="admin-deposit-summary-value">{selectedUser.email}</div>
                    </div>
                    <div className="admin-deposit-summary-item">
                      <label>Current Wallet Balance</label>
                      <div className="admin-deposit-summary-value">
                        {formatCurrency(selectedUser.balances?.wallet || 0)} USDT
                      </div>
                    </div>
                    <div className="admin-deposit-summary-item">
                      <label>Total Balance</label>
                      <div className="admin-deposit-summary-value">
                        {formatCurrency((selectedUser.balances?.wallet || 0) + (selectedUser.balances?.commission || 0))} USDT
                      </div>
                      <small style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        (Wallet + Commission included)
                      </small>
                    </div>
                  </div>

                  <div className="admin-deposit-field">
                    <label htmlFor="depositAmount" className="admin-deposit-label">
                      <i className="bi bi-currency-dollar me-2"></i>
                      Deposit Amount (USDT)
                    </label>
                    <div className="admin-deposit-amount-input">
                      <input
                        type="number"
                        id="depositAmount"
                        step="0.01"
                        min="0.01"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        className="admin-deposit-input"
                        placeholder="0.00"
                        required
                      />
                      <span className="admin-deposit-currency">USDT</span>
                    </div>
                    {depositAmount && parseFloat(depositAmount) > 0 && (
                      <div className="admin-deposit-preview">
                        <div className="admin-deposit-preview-item">
                          <span>Current Balance:</span>
                          <span>{formatCurrency(selectedUser.balances?.wallet || 0)} USDT</span>
                        </div>
                        <div className="admin-deposit-preview-item">
                          <span>Deposit Amount:</span>
                          <span className="text-success">+{formatCurrency(depositAmount)} USDT</span>
                        </div>
                        <div className="admin-deposit-preview-item admin-deposit-preview-total">
                          <span>New Wallet Balance:</span>
                          <span className="admin-deposit-preview-total-value">
                            {formatCurrency((selectedUser.balances?.wallet || 0) + parseFloat(depositAmount || 0))} USDT
                          </span>
                        </div>
                        <div className="admin-deposit-preview-item admin-deposit-preview-total">
                          <span>New Total Balance:</span>
                          <span className="admin-deposit-preview-total-value">
                            {formatCurrency(((selectedUser.balances?.wallet || 0) + parseFloat(depositAmount || 0)) + (selectedUser.balances?.commission || 0))} USDT
                          </span>
                          <small style={{ fontSize: '0.75rem', color: '#6c757d', display: 'block', marginTop: '4px' }}>
                            (Wallet + Commission, excluding frozen)
                          </small>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="admin-deposit-field">
                    <label htmlFor="reason" className="admin-deposit-label">
                      <i className="bi bi-file-text me-2"></i>
                      Reason for Deposit <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="admin-deposit-textarea"
                      rows="4"
                      placeholder="Enter reason for this deposit (e.g., User submitted deposit request via customer service, Transaction ID: xxx)"
                      required
                    />
                    <small className="admin-deposit-hint">
                      This will be logged in the audit trail for compliance purposes
                    </small>
                  </div>

                  <div className="admin-deposit-actions">
                    <button
                      type="submit"
                      className="admin-deposit-btn admin-deposit-btn-primary"
                      disabled={processing || !depositAmount || !reason.trim()}
                    >
                      {processing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Process Deposit
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="admin-deposit-btn admin-deposit-btn-secondary"
                      onClick={() => {
                        setSelectedUser(null);
                        setDepositAmount('');
                        setReason('');
                        setError('');
                        setSuccess('');
                      }}
                      disabled={processing}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Clear
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <div className="admin-deposit-empty-panel">
              <i className="bi bi-arrow-left"></i>
              <p>Select a user from the list to process a deposit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DepositManagement;

